import CommunicationHandler, { EventFilter, SubscriptionHandler } from '@mirage-projects/communication-library';
import { RunnerCondition, ParamObj, GlobalData, MockData, RunnerConditionParamFunction } from '@mirage-projects/cw-models';

import { RunnerData } from './MocksTypes';
import { Process } from './Process';

/**
 * Class to manage the process run
 */
export class Runner {
    private started: boolean;
    private process: Process | string;
    private startCondition: RunnerCondition;
    private finishCondition: RunnerCondition;
    private params: ParamObj;
    private globalData?: GlobalData;
    private subsStart?: SubscriptionHandler;
    private subsEnd?: SubscriptionHandler;

    constructor(data: RunnerData) {
        this.process = data.process;
        this.startCondition = data.startCondition;
        this.finishCondition = data.finishCondition;
        this.globalData = data.globalData ?? {};
        this.params = data.params ?? {};

        this.started = false;
    }

    /**
     * Loads the process
     * @param communicationHandler Handles the communication events
     * @returns promise with the result of the process
     */
    async run(communicationHandler: CommunicationHandler): Promise<MockData[]> {
        this.started = false;

        const result = await this.handleStart(communicationHandler);

        return new Promise((resolve) => {
            // never finishes
            if (!this.finishCondition) {
                return;
            }

            if (!(this.process as Process).run) {
                return;
            }

            if (typeof this.finishCondition === 'function') {
                this.finishCondition(() => {
                    this.kill();
                    resolve(result);
                    return Promise.resolve(result);
                });
                return;
            }

            this.subsEnd = communicationHandler.onEvents(this.finishCondition).subscribe(() => {
                // avoid finish the task before has been started
                if (!this.started) {
                    return;
                }

                this.kill();
                resolve(result);
            });
        });
    }

    private async handleStart(communicationHandler: CommunicationHandler): Promise<MockData[]> {
        if (typeof this.startCondition === 'function') {
            return this.startCondition((...inputData: GlobalData[]) => {
                const globalData = inputData?.[0] ?? {};
                this.globalData = { ...this.globalData, ...globalData };

                return this.handleProcessRun(communicationHandler);
            });
        }

        if (this.startCondition) {
            return new Promise((resolve, reject) => {
                this.subsStart = communicationHandler.onEvents(this.startCondition as string | EventFilter).subscribe(async (e) => {
                    this.params = { ...this.params, ...e.message };
                    try {
                        const result = await this.handleProcessRun(communicationHandler);
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                });
            });
        }

        return await this.handleProcessRun(communicationHandler);
    }

    private async handleProcessRun(communicationHandler: CommunicationHandler): Promise<MockData[]> {
        this.started = true;
        return await (this.process as Process).run(communicationHandler, this.params, this.globalData);
    }

    private kill(): void {
        if (this.subsStart) {
            this.subsStart.cancel();
        }

        if (this.subsEnd) {
            this.subsEnd.cancel();
        }
    }
}

/**
 * Creates a condition function in the window object
 * @param path function condition definition path
 */
export function createWindowFunctionCondition(path: string): RunnerCondition {
    return (fn: RunnerConditionParamFunction): Promise<MockData[]> => {
        (window as any)[path] = (...params: any[]): Promise<MockData[]> => fn(...params);
        return Promise.resolve([]);
    };
}
